import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TestSetupModule, FrameSizeSelectorComponent } from '@nrwl/test-setup';
import { CommonLibModule, DialogService } from '@nrwl/common-lib';
import { CommonMaterialModule } from '@nrwl/common-material';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  CODEC_SELECTION,
  COMPRESSION_TYPE_SELECTION,
  SAMPLING_RATE_SELECTION,
  STREAM_TYPE_SELECTION,
  VIDEO_CHANNEL_RANGES,
  VIDEO_TYPE_SELECTION
} from '../../models/models';

@Component({
  selector: 'nrwl-udp-stream-type',
  standalone: true,
  imports: [CommonModule, TestSetupModule, CommonMaterialModule, FormsModule, CommonLibModule],
  templateUrl: './udp-stream-type.component.html',
  styleUrls: ['./udp-stream-type.component.scss']
})
export class UdpStreamTypeComponent implements OnInit {
  @ViewChild('setupForm', { static: true }) form: NgForm;
  streamTypeSelection = STREAM_TYPE_SELECTION;
  videoTypeSelection = VIDEO_TYPE_SELECTION;
  compressionSelection = COMPRESSION_TYPE_SELECTION;
  codecSelection = CODEC_SELECTION;
  samplingRateSelection = SAMPLING_RATE_SELECTION;
  videoChannelsRanges = VIDEO_CHANNEL_RANGES;
  minCalls: any;
  maxCalls: any;
  minChannels: any;
  maxChannels: any;
  direction = '';
  streamRateConvention = '';
  maxBandWidth = null;
  vlan = 46;
  minL2IrA2B: any;
  maxL2IrA2B: any;
  derivedL1UdpBwA2B: any;
  derivedL1UdpBwB2A: any;
  derivedL2IrA2B: any;
  derivedL2IrB2A: any;
  isL2IrA2B = false;
  isL2IrB2A = false;
  boolean = false;
  isL1ULRBandwidthMaxA2B = false;
  isL1ULRBandwidthMaxB2A = false;
  streamFrameSize = '';
  availablePorts = [];
  isValidPortNumber = false;
  frameSizesObj: any;
  formField = {
    streamType: null,
    videoType: null,
    compression: null,
    channels: null,
    codec: null,
    samplingRate: null,
    calls: null,
    frameSize: null,
    l2IrA2B: null,
    udpBwA2B: null,
    l2IrB2A: null,
    udpBwB2A: null,
    packetLossRatio: null,
    portNumber: null,
    jitterThreshold: null
  };

  constructor(
    private dialog: MatDialog,
    private readonly dialogService: DialogService,
    private streamTypeDialogRef: MatDialogRef<UdpStreamTypeComponent>,
    // eslint-disable-next-line
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    if (data) {
      this.formField.streamType = this.data?.element?.streamType;
      this.formField.l2IrA2B = this.data?.element?.streamL2IrA2B;
      this.formField.udpBwA2B = this.data?.element?.streamUdpBwA2B;
      this.formField.l2IrB2A = this.data?.element?.streamL2IrB2A;
      this.formField.udpBwB2A = this.data?.element?.streamUdpBwB2A;
      this.formField.packetLossRatio = this.data?.element?.streamPacketLossRatio;
      this.formField.portNumber = this.data?.element?.streamUdpPort;
      this.formField.jitterThreshold = this.data?.element?.streamJitterThreshold;
      this.direction = this.data?.direction;
      this.streamRateConvention = this.data?.streamRateConvention;
      this.formField.frameSize = this.data?.element?.streamFrameSize;
      this.vlan = this.data?.vlan;
      this.maxBandWidth = this.data?.maxBandWidth;
      this.availablePorts = this.data?.availablePorts;
      this.frameSizesObj = this.data?.frameSizesObj;
      if (this.formField.streamType === 'Video') {
        this.formField.videoType = this.data?.element?.streamVideoType;
        this.formField.compression = this.data?.element?.streamCompression;
        this.formField.channels = this.data?.element?.streamChannels;
      }
      if (this.formField.streamType === 'Voice') {
        this.formField.codec = this.data?.element?.streamCodec;
        this.formField.samplingRate = this.data?.element?.streamSamplingRate;
        this.formField.calls = this.data?.element?.streamCalls;
      }
      if (this.streamRateConvention === 'l1-mbps') {
        this.minL2IrA2B = 0.1;
        this.maxL2IrA2B = 10000;
      }
      if (this.direction === 'concurrent-bidirectional') {
        this.streamTypeSelection = this.streamTypeSelection.filter(type => type.name !== 'Video');
      }
      if (this.direction === 'unidirectional-a2b' || this.direction === 'unidirectional-b2a') {
        this.streamTypeSelection = this.streamTypeSelection.filter(type => type.name !== 'Voice');
      }
      this.calcAndUpdateBandwidth('udpBwA2B');
      this.calcAndUpdateBandwidth('udpBwB2A');
    }
  }
  // eslint-disable-next-line
  ngOnInit(): void {}

  onL2IrA2BChange(): void {
    let isValid = false;
    if (!isNaN(this.formField.l2IrA2B)) {
      let value = parseFloat(this.formField.l2IrA2B);
      if (!isNaN(value)) {
        value = Math.round(value * 100) / 100;
        value = this.roundToPrecision(value, 2);
        if (value >= this.minL2IrA2B && value <= this.maxL2IrA2B) {
          isValid = true;
          this.formField.l2IrA2B = value;
        }
        if (isValid && this.streamRateConvention === 'l1-mbps') {
          this.calcAndUpdateBandwidth('udpBwA2B');
        } else if (this.streamRateConvention === 'l2-mbps') {
          this.derivedL2IrA2B = this.calculateL1ULRBandwidthFromL4CIRAndFrameSize(
            this.formField.l2IrA2B
          );
          if (this.derivedL2IrA2B <= this.maxBandWidth) {
            this.isL2IrA2B = false;
            this.formField.l2IrA2B = value;
            this.calcAndUpdateBandwidth('udpBwA2B');
          } else {
            this.isL2IrA2B = true;
          }
        }
      }
    }
  }

  onUdpBwA2BChange(): void {
    if (!isNaN(this.formField.udpBwA2B)) {
      let value = parseFloat(this.formField.udpBwA2B);
      if (!isNaN(value)) {
        value = Math.round(value * 100) / 100;
        value = this.roundToPrecision(value, 2);
        this.formField.udpBwA2B = value;
        this.derivedL1UdpBwA2B = this.calculateL1ULRBandwidthFromL4CIRAndFrameSize(value);
        if (this.derivedL1UdpBwA2B <= this.maxBandWidth) {
          this.isL1ULRBandwidthMaxA2B = false;
          this.calcAndUpdateBandwidth('l2IrA2B');
        } else {
          this.isL1ULRBandwidthMaxA2B = true;
        }
      }
    }
  }

  onL2IrB2AChange(): void {
    let isValid = false;
    if (!isNaN(this.formField.l2IrB2A)) {
      let value = parseFloat(this.formField.l2IrB2A);
      if (!isNaN(value)) {
        value = Math.round(value * 100) / 100;
        value = this.roundToPrecision(value, 2);
        this.formField.l2IrB2A = value;
        if (value >= this.minL2IrA2B && value <= this.maxL2IrA2B) {
          isValid = true;
          this.formField.l2IrB2A = value;
        }
      }
      if (isValid && this.streamRateConvention === 'l1-mbps') {
        this.calcAndUpdateBandwidth('udpBwB2A');
      } else if (this.streamRateConvention === 'l2-mbps') {
        this.derivedL2IrB2A = this.calculateL1ULRBandwidthFromL4CIRAndFrameSize(
          this.formField.l2IrB2A
        );
        if (this.derivedL2IrB2A <= this.maxBandWidth) {
          this.isL2IrB2A = false;
          this.calcAndUpdateBandwidth('udpBwB2A');
        } else {
          this.isL2IrB2A = true;
        }
      }
    }
  }

  onUdpBwB2AChange(): void {
    if (!isNaN(this.formField.udpBwB2A)) {
      let value = parseFloat(this.formField.udpBwB2A);
      if (!isNaN(value)) {
        value = Math.round(value * 100) / 100;
        value = this.roundToPrecision(value, 2);
        this.formField.udpBwB2A = value;
        this.derivedL1UdpBwB2A = this.calculateL1ULRBandwidthFromL4CIRAndFrameSize(value);
        if (this.derivedL1UdpBwB2A <= this.maxBandWidth) {
          this.isL1ULRBandwidthMaxB2A = false;
          this.calcAndUpdateBandwidth('l2IrB2A');
        } else {
          this.isL1ULRBandwidthMaxB2A = true;
        }
      }
    }
  }
  // eslint-disable-next-line
  calculateL1ULRBandwidthFromL4CIRAndFrameSize(fieldValue): any {
    const frameSize = parseInt(this.removeCustom(this.formField.frameSize));
    let udpBw = (fieldValue / (frameSize - this.vlan)) * (frameSize + 20);
    udpBw = this.roundToPrecision(udpBw, 2);
    return udpBw;
  }
  // eslint-disable-next-line
  calcAndUpdateBandwidth(type): void {
    const frameSize = parseInt(this.removeCustom(this.formField.frameSize));
    const strmL2IrA2BVal = this.formField?.l2IrA2B;
    const strmL2IrB2AVal = this.formField?.l2IrB2A;
    const precision = 2;
    const ethFrameOverhead = this.vlan;
    let result: any = '';
    if (frameSize && this.streamRateConvention) {
      switch (type) {
        case 'udpBwA2B':
          if (this.streamRateConvention && this.streamRateConvention === 'l1-mbps') {
            result = (strmL2IrA2BVal / (frameSize + 20)) * (frameSize - ethFrameOverhead);
          } else {
            result = (strmL2IrA2BVal / frameSize) * (frameSize - ethFrameOverhead);
          }
          result = this.roundToPrecision(result, precision);
          this.formField.udpBwA2B = result;
          this.isL2IrA2B = false;
          break;
        case 'udpBwB2A':
          if (this.streamRateConvention && this.streamRateConvention === 'l1-mbps') {
            result = (strmL2IrB2AVal / (frameSize + 20)) * (frameSize - ethFrameOverhead);
          } else {
            result = (strmL2IrB2AVal / frameSize) * (frameSize - ethFrameOverhead);
          }
          result = this.roundToPrecision(result, precision);
          this.formField.udpBwB2A = result;
          this.isL1ULRBandwidthMaxB2A = false;
          break;
        case 'l2IrA2B':
          // eslint-disable-next-line
          const strmUdpBwA2BVal = this.formField.udpBwA2B;
          if (this.streamRateConvention && this.streamRateConvention === 'l1-mbps') {
            result = (strmUdpBwA2BVal / (frameSize - ethFrameOverhead)) * (frameSize + 20);
          } else {
            result = (strmUdpBwA2BVal * frameSize) / (frameSize - ethFrameOverhead);
          }
          result = this.roundToPrecision(result, precision);
          this.formField.l2IrA2B = result;
          this.form.controls?.['l2IrA2B']?.markAsTouched();
          this.isL2IrA2B = false;
          break;
        case 'l2IrB2A':
          // eslint-disable-next-line
          const strmUdpBwB2AVal = this.formField.udpBwB2A;
          if (this.streamRateConvention && this.streamRateConvention === 'l1-mbps') {
            result = (strmUdpBwB2AVal / (frameSize - ethFrameOverhead)) * (frameSize + 20);
          } else {
            result = (strmUdpBwB2AVal * frameSize) / (frameSize - ethFrameOverhead);
          }
          result = this.roundToPrecision(result, precision);
          this.formField.l2IrB2A = result;
          this.form.controls?.['l2IrB2A']?.markAsTouched();
          this.isL2IrB2A = false;
          break;
        default:
          // eslint-disable-next-line
          console.log('ERROR: calcAndUpdateBandwidth - Unknown type requested: ' + type);
          break;
      }
    }
  }
  // eslint-disable-next-line
  roundToPrecision(number, precision): any {
    if (!number || !precision) {
      return number;
    }
    return (+(Math.round(+(number + 'e' + precision)) + 'e' + -precision)).toFixed(precision);
  }
  // eslint-disable-next-line
  removeCustom(str): any {
    if (str?.indexOf('custom:') >= 0) {
      // eslint-disable-next-line
      let parts = str.split('custom:');
      return parts.join('');
    }
    return str;
  }

  onstreamTypeChange(): void {
    if (this.formField.streamType === 'Video') {
      this.formField.frameSize = 'custom:1392';
      this.formField.videoType = this.videoTypeSelection[0].value;
      if (this.streamRateConvention === 'l1-mbps') {
        this.formField.l2IrA2B = this.roundToPrecision(7.0, 2);
        this.formField.udpBwA2B = 6.67;
        if (this.vlan === 50) {
          this.formField.l2IrA2B = this.roundToPrecision(7.0, 2);
          this.formField.udpBwA2B = 6.65;
        }
        if (this.vlan === 54) {
          this.formField.l2IrA2B = this.roundToPrecision(7.0, 2);
          this.formField.udpBwA2B = 6.63;
        }
      } else {
        this.formField.l2IrA2B = this.roundToPrecision(6.9, 2);
        this.formField.udpBwA2B = 6.67;
        if (this.vlan === 50) {
          this.formField.l2IrA2B = this.roundToPrecision(6.9, 2);
          this.formField.udpBwA2B = 6.65;
        }
        if (this.vlan === 54) {
          this.formField.l2IrA2B = this.roundToPrecision(6.9, 2);
          this.formField.udpBwA2B = 6.63;
        }
      }
      this.formField.compression = this.compressionSelection[1].value;
      this.formField.channels = 1;
      this.formField.jitterThreshold = 40;
      this.minChannels =
        this.videoChannelsRanges[this.formField.videoType][
          this.formField.compression
        ].numChannels.min;
      this.maxChannels =
        this.videoChannelsRanges[this.formField.videoType][
          this.formField.compression
        ].numChannels.max;
    } else if (this.formField.streamType === 'Voice') {
      this.formField.codec = this.codecSelection[0].value;
      this.formField.frameSize = 'custom:138';
      if (this.data?.tableChange === 'tableA') {
        this.calcAndUpdateBandwidth('udpBwA2B');
      } else {
        this.calcAndUpdateBandwidth('udpBwB2A');
      }
      if (this.streamRateConvention === 'l1-mbps') {
        this.formField.l2IrA2B = 1.27;
        this.formField.udpBwA2B = 0.74;
        if (this.vlan === 50) {
          this.formField.l2IrA2B = 1.33;
          this.formField.udpBwA2B = 0.74;
        }
        if (this.vlan === 54) {
          this.formField.l2IrA2B = 1.39;
          this.formField.udpBwA2B = 0.73;
        }
      } else {
        this.formField.l2IrA2B = 1.11;
        this.formField.udpBwA2B = 0.74;
        if (this.vlan === 50) {
          this.formField.l2IrA2B = 1.16;
          this.formField.udpBwA2B = 0.74;
        }
        if (this.vlan === 54) {
          this.formField.l2IrA2B = 1.22;
          this.formField.udpBwA2B = 0.73;
        }
      }
      this.formField.samplingRate = 10;
      this.formField.calls = 10;
      this.formField.jitterThreshold = 40;
      this.minCalls = this.codecSelection[0].numCalls.min;
      this.maxCalls = this.codecSelection[0].numCalls.max;
    } else {
      this.formField.frameSize = '512';
      if (this.streamRateConvention === 'l1-mbps') {
        this.formField.l2IrA2B = this.roundToPrecision(10.0, 2);
        this.formField.udpBwA2B = 8.76;
        if (this.vlan === 50) {
          this.formField.l2IrA2B = this.roundToPrecision(10.0, 2);
          this.formField.udpBwA2B = 8.68;
        }
        if (this.vlan === 54) {
          this.formField.l2IrA2B = this.roundToPrecision(10.0, 2);
          this.formField.udpBwA2B = 8.61;
        }
      } else {
        this.formField.l2IrA2B = this.roundToPrecision(10.0, 2);
        this.formField.udpBwA2B = this.roundToPrecision(9.1, 2);
        if (this.vlan === 50) {
          this.formField.l2IrA2B = this.roundToPrecision(10.0, 2);
          this.formField.udpBwA2B = 9.02;
        }
        if (this.vlan === 54) {
          this.formField.l2IrA2B = this.roundToPrecision(10.0, 2);
          this.formField.udpBwA2B = 8.95;
        }
      }
      this.formField.jitterThreshold = 0;
    }
  }
  onVideoTypeChange(): void {
    if (this.formField.videoType) {
      this.minChannels =
        this.videoChannelsRanges[this.formField.videoType][
          this.formField.compression
        ].numChannels.min;
      this.maxChannels =
        this.videoChannelsRanges[this.formField.videoType][
          this.formField.compression
        ].numChannels.max;
      this.updateVideoConfig();
    }
  }

  onCompressionChange(): void {
    if (this.formField.compression) {
      this.minChannels =
        this.videoChannelsRanges[this.formField.videoType][
          this.formField.compression
        ].numChannels.min;
      this.maxChannels =
        this.videoChannelsRanges[this.formField.videoType][
          this.formField.compression
        ].numChannels.max;
      this.updateVideoConfig();
    }
  }

  onNumberOfChannelsChange(): void {
    this.updateVideoConfig();
  }

  onCodecTypeChange(): void {
    if (this.formField.codec) {
      const index = this.codecSelection.findIndex(item => item.value === this.formField.codec);
      if (index >= 0) {
        this.minCalls = this.codecSelection[index].numCalls.min;
        this.maxCalls = this.codecSelection[index].numCalls.max;
        this.updateVoiceConfig();
      }
    }
  }

  onPortNumberChange(): void {
    if (this.formField.portNumber) {
      if (
        !this.availablePorts.includes(this.formField.portNumber) &&
        this.formField.portNumber !== this.data?.element?.streamUdpPort
      ) {
        this.isValidPortNumber = true;
      } else {
        this.isValidPortNumber = false;
      }
    }
  }

  onSaveStreamTypeSettingsData(): void {
    if (this.form.valid) {
      const result = {
        streamType: this.formField.streamType,
        packetLossRatio: this.formField.packetLossRatio,
        portNumber: this.formField.portNumber,
        jitterThreshold: this.formField.jitterThreshold,
        l2IrA2B: this.formField.l2IrA2B || this.data?.element?.streamL2IrA2B,
        udpBwA2B: this.formField.udpBwA2B || this.data?.element?.streamUdpBwA2B,
        l2IrB2A: this.formField.l2IrB2A || this.data?.element?.streamL2IrB2A,
        udpBwB2A: this.formField.udpBwB2A || this.data?.element?.streamUdpBwB2A,
        streamFrameSize: this.formField.frameSize,
        videoType: this.formField.videoType,
        compression: this.formField.compression,
        channels: this.formField.channels,
        codec: this.formField.codec,
        samplingRate: this.formField.samplingRate,
        calls: this.formField.calls
      };
      this.streamTypeDialogRef.close({ data: result });
    }
  }

  updateVideoConfig(): void {
    const numberOfChannels = Number(this.formField.channels);
    const precision = 2;
    let totalRate = 0;
    // eslint-disable-next-line
    let thruputMultiplier: number = 0;
    if (this.formField.videoType === 'HDTV') {
      if (this.formField.compression === 'MPEG2') {
        thruputMultiplier = 19;
      } else {
        thruputMultiplier = 7;
      }
    } else {
      if (this.formField.compression === 'MPEG2') {
        thruputMultiplier = 4;
      } else {
        thruputMultiplier = 2;
      }
    }
    totalRate = numberOfChannels * thruputMultiplier;
    totalRate = this.roundToPrecision(totalRate, precision);
    if (this.streamRateConvention === 'l2-mbps') {
      const frameSize = parseInt(this.removeCustom(this.formField.frameSize));
      totalRate = (totalRate / (frameSize + 20)) * frameSize;
      totalRate = this.roundToPrecision(totalRate, 2);
    }
    if (this.data?.tableChange === 'tableA') {
      this.formField.l2IrA2B = totalRate;
      this.calcAndUpdateBandwidth('udpBwA2B');
    } else {
      this.formField.l2IrB2A = totalRate;
      this.calcAndUpdateBandwidth('udpBwB2A');
    }
  }

  updateVoiceConfig(): void {
    const samplingRate = this.formField.samplingRate;
    const numberOfCalls = this.formField.calls;
    this.formField.frameSize = 'custom:' + this.calcFrameSize(samplingRate);
    let totalRate = this.calcTotalRate(samplingRate, numberOfCalls, this.formField.frameSize);
    totalRate = this.roundToPrecision(totalRate, 2);
    if (this.data?.tableChange === 'tableA') {
      this.formField.udpBwA2B = totalRate;
      this.calcAndUpdateBandwidth('l2IrA2B');
    } else {
      this.formField.udpBwB2A = totalRate;
      this.calcAndUpdateBandwidth('l2IrB2A');
    }
  }
  // eslint-disable-next-line
  calcFrameSize(samplingRate): any {
    const codecValue = this.getCodecObject();
    const index = this.codecSelection.findIndex(item => item.value === codecValue);
    if (index >= 0) {
      return (samplingRate * this.codecSelection[index].payload) / 10 + 58;
    }
  }

  getCodecObject(): any {
    let codec = this.formField.codec;
    if (!codec) {
      codec = 'G711';
    }
    if (!codec) {
      // eslint-disable-next-line
      console.log('ERROR: unable to get codecObject for codec: ' + codec);
      return;
    }
    return codec;
  }
  // eslint-disable-next-line
  calcTotalRate = function (samplingRate, numberOfCalls, frameSize) {
    const frmSize = Number(this.removeCustom(frameSize));
    return ((((frmSize - 46) * 8) / samplingRate) * numberOfCalls) / 1000;
  };

  onChangeFrameSize(): void {
    const frameDataObj = {
      rowData: this.frameSizesObj,
      sctFrames: this.updateFrames(this.data?.element),
      sptFrames: this.updateFrames(this.data?.element),
      multiSelectFrames: false,
      emixFrames: false,
      isMultiSelectEnable: false,
      multiSelectFramesEnabled: false
    };
    const dialogRef = this.dialog.open(FrameSizeSelectorComponent, {
      data: frameDataObj,
      disableClose: true
    });
    dialogRef.beforeClosed().subscribe(_res => {
      if (_res) {
        this.formField.frameSize =
          _res?.sctFrames && _res?.sctFrames !== null ? _res?.sctFrames : _res?.sptFrames;
        this.data.element.isMultiSelectEnable = false;
        if (this.data?.tableChange === 'tableA') {
          this.calcAndUpdateBandwidth('l2IrA2B');
        } else {
          this.calcAndUpdateBandwidth('l2IrB2A');
        }
        if (this.data.element.streamFrameSize != this.formField.frameSize) {
          this.form.controls['frameSize'].markAsDirty();
        }
      }
    });
  }
  // eslint-disable-next-line
  updateFrames(rowData: any): any {
    const frames = rowData.streamFrameSize;
    const frameList = this.frameSizesObj?.['frameList'];
    let index = 0;
    const isEmix = false;
    frameList?.forEach(item => {
      index = index + 1;
      if (!isEmix) {
        if (frames.toString().split(',').includes(item.label.toString())) {
          frameList[index - 1]['enable'] = true;
        } else if (item.label.toString() === 'custom' && frames.indexOf('custom') !== -1) {
          frameList[index - 1]['enable'] = true;
          frameList[index - 1]['value'] = rowData.streamFrameSize
            .split('custom:')[1]
            .replace(',', '');
        } else {
          frameList[index - 1]['enable'] = false;
        }
      }
    });
    return frameList;
  }

  onClickCancel(): void {
    if (this.form.dirty) {
      const param = {
        id: 'setupService.udp_stream_type_settings',
        callBackService: this,
        callBackFuntion: this.onCloseConfirmDialog
      };
      this.dialogService.showDialog('confirm', 'body', param);
    } else {
      this.streamTypeDialogRef.close();
    }
  }
  private onCloseConfirmDialog = () => {
    this.streamTypeDialogRef.close();
  };
}
